import { Close as CloseIcon, History as HistoryIcon } from '@mui/icons-material';
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  RadioGroup,
  Tooltip,
} from '@mui/material';
import React from 'react';
import { Palette } from 'Palette';
import { StyledRadio } from '../styles/StyledInputs';
import StyledSection from '../styles/StyledSection';
import { StyledHeading } from '../styles/styles';
import { ActivityLogFromAPI, ComparisonMode } from './fullContentDiffViewerTypes';
import { formatVersionTimestamp, getEventTypeLabel } from './fullContentDiffViewerUtils';

interface FullContentDiffViewerControlsProps {
  tone: keyof typeof Palette;
  comparisonMode: ComparisonMode;
  onComparisonModeChange: (mode: ComparisonMode) => void;
  loadingLatestDraft: boolean;
  loadingPublished: boolean;
  selectedVersion: ActivityLogFromAPI | null;
  isSelectedVersionLatest: () => boolean;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  historyButtonRef: React.RefObject<HTMLButtonElement>;
  onBackToOriginal: () => void;
}

const FullContentDiffViewerControls: React.FC<FullContentDiffViewerControlsProps> = ({
  tone,
  comparisonMode,
  onComparisonModeChange,
  loadingLatestDraft,
  loadingPublished,
  selectedVersion,
  isSelectedVersionLatest,
  sidebarOpen,
  onToggleSidebar,
  historyButtonRef,
  onBackToOriginal,
}) => {
  const latestDraftDisabled = !selectedVersion || isSelectedVersionLatest() || loadingLatestDraft;

  return (
    <StyledSection tone={tone}>
      <Box sx={{ mb: 2 }}>
        <FormControl component="fieldset">
          <FormLabel component="legend" sx={{ color: Palette[tone].text, mb: 1 }}>
            Compare to:
          </FormLabel>
          <RadioGroup
            row
            value={comparisonMode}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onComparisonModeChange(e.target.value as ComparisonMode)
            }
            sx={{ gap: 2 }}
          >
            <FormControlLabel
              value="previous"
              control={<StyledRadio />}
              label="Previous Version"
              sx={{ color: Palette[tone].text }}
            />
            <FormControlLabel
              value="latest-draft"
              control={<StyledRadio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Latest Draft
                  {loadingLatestDraft && (
                    <CircularProgress size={12} sx={{ color: Palette[tone].text }} />
                  )}
                </Box>
              }
              disabled={latestDraftDisabled}
              sx={{
                color: Palette[tone].text,
                opacity: latestDraftDisabled ? 0.5 : 1,
              }}
            />
            <FormControlLabel
              value="published"
              control={<StyledRadio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Published Version
                  {loadingPublished && (
                    <CircularProgress size={12} sx={{ color: Palette[tone].text }} />
                  )}
                </Box>
              }
              disabled={loadingPublished}
              sx={{
                color: Palette[tone].text,
                opacity: loadingPublished ? 0.5 : 1,
              }}
            />
          </RadioGroup>
        </FormControl>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip title={sidebarOpen ? 'Hide Version History' : 'View Version History'}>
            <IconButton
              ref={historyButtonRef}
              onClick={onToggleSidebar}
              sx={{
                color: Palette[tone].text,
                backgroundColor: sidebarOpen ? Palette[tone].activeBg : Palette[tone].background,
                border: `1px solid ${Palette[tone].border}`,
                '&:hover': {
                  backgroundColor: Palette[tone].activeBg,
                },
              }}
            >
              <HistoryIcon />
            </IconButton>
          </Tooltip>
          <StyledHeading variant="h5" tone={tone}>
            Content Diff
          </StyledHeading>
        </Box>
        {selectedVersion && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`${getEventTypeLabel(selectedVersion.eventType)} - ${formatVersionTimestamp(selectedVersion.eventTimestamp)}`}
              size="small"
              variant="filled"
              sx={{
                color: Palette[tone].background,
                backgroundColor: Palette[tone].text,
                fontWeight: 500,
              }}
            />
            <Tooltip title="Back to Original Content">
              <IconButton
                onClick={onBackToOriginal}
                size="small"
                sx={{
                  color: Palette[tone].text,
                  backgroundColor: Palette[tone].background,
                  border: `1px solid ${Palette[tone].border}`,
                  '&:hover': {
                    backgroundColor: Palette[tone].activeBg,
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </StyledSection>
  );
};

export default FullContentDiffViewerControls;
